import { computed, type ComputedRef, type Ref } from 'vue'
import { isNativePlatform } from '../platform/capacitorDetector'
import { useCookie, useState } from '#app'

/**
 * Согласие на использование cookie и аналитики.
 *
 * До этого модуля баннер был декоративным: чекбоксы стояли отмеченными, их значение
 * никуда не сохранялось, а Метрика грузилась безусловно — то есть аналитические
 * cookie ставились до любого выбора. Интерфейс, показывающий выбор, которого нет,
 * при проверке хуже, чем отсутствие баннера: ч. 1 ст. 9 152-ФЗ требует, чтобы
 * согласие было конкретным и информированным, а доказывать его обязан оператор.
 *
 * Здесь хранится ТОЛЬКО решение пользователя. Гейт стоит в потребителях согласия
 * (`plugins/yandex-metrika.client.ts`, `plugins/analytics.client.ts`), чтобы shared
 * не знал ни про Метрику, ни про свой event-store.
 */

/** Версия набора категорий. Растёт, когда меняется состав категорий или их смысл. */
export const COOKIE_CONSENT_VERSION = 1

/** Имя cookie с решением. Меняется только вместе с форматом значения. */
export const COOKIE_CONSENT_KEY = 'cookie_consent'

/**
 * Полгода — компромисс между «не дёргать человека на каждом заходе» и практикой
 * повторного запроса согласия. По истечении баннер покажется снова.
 */
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180

export type CookieCategory = 'necessary' | 'analytics' | 'marketing'

export interface CookieConsentValue {
  /** Версия набора категорий на момент выбора. */
  v: number
  /** Метрика, вебвизор, свой event-store. */
  analytics: boolean
  /** Рекламные метки и ретаргетинг. */
  marketing: boolean
  /** Когда человек сделал выбор — нужно как доказательство согласия. */
  ts: string
}

export interface CookieConsentApi {
  /** Текущее решение или `null`, пока выбор не сделан. */
  consent: Ref<CookieConsentValue | null>
  /** Выбор сделан и он актуальной версии. */
  isDecided: ComputedRef<boolean>
  /** Показывать ли баннер. */
  shouldAsk: ComputedRef<boolean>
  analyticsAllowed: ComputedRef<boolean>
  marketingAllowed: ComputedRef<boolean>
  /** Согласие на все необязательные категории. */
  acceptAll: () => void
  /** Отказ от всех необязательных категорий — равнозначная альтернатива согласию. */
  rejectAll: () => void
  /** Сохранить точечный выбор из настроек баннера. */
  save: (choice: { analytics: boolean; marketing: boolean }) => void
  /** Сбросить решение и снова показать баннер (ссылка «Настройки cookie»). */
  reopen: () => void
}

/** Значение из cookie могло быть записано старой версией или руками — проверяем форму. */
const isUsableConsent = (value: unknown): value is CookieConsentValue => {
  if (!value || typeof value !== 'object') return false

  const candidate = value as Partial<CookieConsentValue>

  return (
    candidate.v === COOKIE_CONSENT_VERSION
    && typeof candidate.analytics === 'boolean'
    && typeof candidate.marketing === 'boolean'
  )
}

/**
 * Решение по cookie.
 *
 * Состояние живёт в `useState`, чтобы плагины и баннер видели один и тот же объект
 * и реагировали на изменение сразу: без общего состояния отзыв согласия применился бы
 * только со следующей перезагрузки, а это уже не «прекратить обработку».
 */
export const useCookieConsent = (): CookieConsentApi => {
  const cookie = useCookie<CookieConsentValue | null>(COOKIE_CONSENT_KEY, {
    maxAge: CONSENT_MAX_AGE_SECONDS,
    sameSite: 'lax',
    // Само согласие читается на клиенте, но SSR должен знать о нём, иначе баннер
    // мигает на каждой загрузке у тех, кто уже ответил.
    default: () => null,
  })

  const consent = useState<CookieConsentValue | null>('cookie-consent', () =>
    isUsableConsent(cookie.value) ? cookie.value : null,
  )

  const write = (analytics: boolean, marketing: boolean): void => {
    const value: CookieConsentValue = {
      v: COOKIE_CONSENT_VERSION,
      analytics,
      marketing,
      ts: new Date().toISOString(),
    }

    consent.value = value
    cookie.value = value
  }

  const isDecided = computed(() => isUsableConsent(consent.value))

  /**
   * Что считать разрешённым, пока человек не ответил.
   *
   * В вебе — ничего: там работает Яндекс.Метрика, то есть данные уходят третьему
   * лицу, и до согласия этого делать нельзя. Поэтому баннер и стоит.
   *
   * В приложении Метрика выключена (в мобильной сборке нет счётчика), остаётся
   * только собственная аналитика — первая сторона, свой сервер, без передачи
   * наружу. Она ведётся на законном интересе (п. 7 ч. 1 ст. 6 152-ФЗ), а отказаться
   * можно тумблером «Аналитика» в разделе «Безопасность». Баннер про «файлы cookie»
   * в приложении поэтому не показывается — он там и не о чем.
   */
  const defaultAnalytics = isNativePlatform()

  return {
    consent,
    isDecided,
    shouldAsk: computed(() => !isDecided.value),
    analyticsAllowed: computed(() =>
      isUsableConsent(consent.value) ? consent.value.analytics : defaultAnalytics,
    ),
    marketingAllowed: computed(() => consent.value?.marketing === true),
    acceptAll: () => write(true, true),
    rejectAll: () => write(false, false),
    save: choice => write(choice.analytics, choice.marketing),
    reopen: () => {
      consent.value = null
      cookie.value = null
    },
  }
}
